import { createActionGroup, props } from '@ngrx/store';
import { AttackScenario } from '../../domain/attack-scenario';
import {
  DefensiveReaction,
  EventType,
  GameIncidentReason,
  GameIncidentSituation,
  GameIncidentType,
  GamePeriod,
  HighlightPlayback,
  HighlightRating,
  HighlightType,
  InterruptionType,
  NetImpactPositionCoordinate,
  OddMenRushDetail,
  OfficialsCallAction,
  OfficialsCallSituation,
  PassOutcome,
  PassType,
  PenaltyDuration,
  PenaltyType,
  PositionCoordinate,
  ShotOutcome,
  ShotScenario,
  ShotType,
  StrengthState,
  TeamFaceOffOutcome,
  VideoReviewType,
  VideoTagType
} from '../../domain/game-event';
import {
  GameRestartReason,
  GameRestartType
} from '../../domain/game-restart';
import { PitchId } from '../../domain/pitch';
import { TurnoverScenario, TurnoverType } from '../../domain/turnover';
import { Detection } from '../../services/detection';
import ObjectId from 'bson-objectid';

const eventActionGroup = createActionGroup({
  source: 'Event Actions',
  events: {
    'Event Id Change': props<{ _id: string }>(),
    'Game Time Change': props<{ gameTime: number }>(),
    // Update videoTime after seeking. Should only be called from video player.
    'Video Time Change': props<{ videoTime: number }>(),
    'Period Change': props<{ period: GamePeriod }>(),
    'Strength State Change': props<{ strengthState: StrengthState }>(),
    'Event Type Change': props<{ eventType: EventType }>(),
    'Team Change': props<{ team: string }>(),
    'Is Home Team Empty Net Change': props<{ isHomeTeamEmptyNet: boolean }>(),
    'Is Away Team Empty Net Change': props<{ isAwayTeamEmptyNet: boolean }>(),
    'Odd Men Rush Detail Change': props<{
      oddMenRushDetail: OddMenRushDetail;
    }>(),

    'Position Change': props<{ position: PositionCoordinate }>(),
    'Position Pitch Change': props<{ positionPitch: PitchId }>(),
    'Pass Receiver Position Change': props<{
      passReceiverPosition: PositionCoordinate;
    }>(),
    'Shot Blocker Position Change': props<{
      shotBlockerPosition: PositionCoordinate;
    }>(),
    'Faceoff Outcome Position Change': props<{
      faceoffOutcomePosition: PositionCoordinate;
    }>(),

    'Pass Type Change': props<{ passType: PassType }>(),
    'Pass Outcome Change': props<{ passOutcome: PassOutcome }>(),

    'Shot Outcome Change': props<{ shotOutcome: ShotOutcome }>(),
    'Shot Scenario Change': props<{ shotScenario: ShotScenario }>(),
    'Shot Type Change': props<{ shotType: ShotType }>(),
    'Attack Scenario Change': props<{ attackScenario: AttackScenario}>(),

    'Is Blocked Change': props<{ is_blocked: string }>(),
    'Has Net Traffic Change': props<{ has_net_traffic: string }>(),
    'Has Screen Change': props<{ has_screen: string }>(),

    'Team FaceOff Outcome Change': props<{
      teamFaceOffOutcome: TeamFaceOffOutcome;
    }>(),

    'Interruption Type Change': props<{
      interruption_type: InterruptionType;
    }>(),

    'Video Tag Change': props<{ videoTag: VideoTagType }>(),
    'Defensive Reaction Change': props<{
      defensiveReaction: DefensiveReaction;
    }>(),
    'Game Restart Type Change': props<{ gameRestartType: GameRestartType }>(),
    'Game Restart Reason Change': props<{
      gameRestartReason: GameRestartReason;
    }>(),
    'Turnover Type Change': props<{ turnoverType: TurnoverType }>(),
    'Turnover Scenario Change': props<{
      turnoverScenario: TurnoverScenario;
    }>(),

    'Player Number Change': props<{ playerNumber: string }>(),
    'Pass Receiver Player Number Change': props<{
      passReceiverPlayerNumber: string;
    }>(),
    'Assist 1 Player Number Change': props<{
      assist1PlayerNumber: string;
    }>(),
    'Assist 2 Player Number Change': props<{
      assist2PlayerNumber: string;
    }>(),
    'Shot Blocker Player Number Change': props<{
      shotBlockerPlayerNumber: string;
    }>(),
    'Deflector Player Number Change': props<{
      deflectorPlayerNumber: string;
    }>(),
    'Screener Player Number Change': props<{ screenerPlayerNumber: string }>(),
    'Net Traffic Causer Player Number Change': props<{
      netTrafficCauserPlayerNumber: string;
    }>(),
    'FaceOff Opponent Player Number Change': props<{
      faceOffOpponentPlayerNumber: string;
    }>(),
    'Fouled Player Number Change': props<{ fouledPlayerNumber: string }>(),
    'Turnover Opponent Player Number Change': props<{
      turnoverOpponentPlayerNumber: string;
    }>(),
    'Official 1 Number Change': props<{
      officialId: number;
      remove?: boolean;
    }>(),
    'Official 2 Number Change': props<{
      officialId: number;
      remove?: boolean;
    }>(),

    'Selected Track Id Change': props<{ selectedTrackId: number }>(),

    'Detections Change': props<{ detections: Detection[] }>(),

    'Highlight Type Change': props<{ highlightType: HighlightType }>(),
    'Video Review Type Change': props<{ videoReviewType: VideoReviewType }>(),
    'Highlight Playback Change': props<{
      highlightPlayback: HighlightPlayback;
    }>(),
    'Highlight Rating Change': props<{ highlightRating: HighlightRating }>(),

    'Net Impact Change': props<{
      netImpact: NetImpactPositionCoordinate;
    }>(),

    'Penalty Type Change': props<{
      penaltyType: PenaltyType;
    }>(),
    'Penalty Id Change': props<{
      penaltyId: ObjectId;
    }>(),
    'Penalty Duration Change': props<{
      penaltyDuration: PenaltyDuration;
    }>(),

    'Game Incident Reason Change': props<{
      gameIncidentReason: GameIncidentReason;
    }>(),
    'Game Incident Situation Change': props<{
      gameIncidentSituation: GameIncidentSituation;
    }>(),
    'Game Incident Type Change': props<{
      gameIncidentType: GameIncidentType;
    }>(),
    'Officials Call Situation Change': props<{
      officialsCallSituation: OfficialsCallSituation;
    }>(),
    'Officials Call Action Change': props<{
      officialsCallAction: OfficialsCallAction;
    }>(),

    'Reset State': props<{ team: string; period: GamePeriod }>(),
    'Reset Hard State': props<any>()
  }
});

export const {
  eventIdChange,
  gameTimeChange,
  videoTimeChange,
  periodChange,
  strengthStateChange,
  eventTypeChange,
  teamChange,
  isHomeTeamEmptyNetChange,
  isAwayTeamEmptyNetChange,
  oddMenRushDetailChange,
  positionChange,
  positionPitchChange,
  passReceiverPositionChange,
  shotBlockerPositionChange,
  faceoffOutcomePositionChange,
  passTypeChange,
  passOutcomeChange,
  shotOutcomeChange,
  attackScenarioChange,
  shotScenarioChange,
  shotTypeChange,
  isBlockedChange,
  hasNetTrafficChange,
  hasScreenChange,
  teamFaceoffOutcomeChange,
  interruptionTypeChange,
  videoTagChange,
  defensiveReactionChange,
  gameRestartTypeChange,
  gameRestartReasonChange,
  turnoverTypeChange,
  turnoverScenarioChange,
  playerNumberChange,
  passReceiverPlayerNumberChange,
  assist1PlayerNumberChange,
  assist2PlayerNumberChange,
  shotBlockerPlayerNumberChange,
  deflectorPlayerNumberChange,
  screenerPlayerNumberChange,
  netTrafficCauserPlayerNumberChange,
  faceoffOpponentPlayerNumberChange,
  fouledPlayerNumberChange,
  turnoverOpponentPlayerNumberChange,
  official1NumberChange,
  official2NumberChange,
  selectedTrackIdChange,
  detectionsChange,
  highlightTypeChange,
  videoReviewTypeChange,
  highlightPlaybackChange,
  highlightRatingChange,
  netImpactChange,
  penaltyTypeChange,
  penaltyIdChange,
  penaltyDurationChange,
  gameIncidentReasonChange,
  gameIncidentSituationChange,
  gameIncidentTypeChange,
  officialsCallActionChange,
  officialsCallSituationChange,
  resetState,
  resetHardState
} = eventActionGroup;
